import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminAPI, AiMetric, AiSummary, Evaluator } from "@/app/apis/admin";
import { HardDrive, FileText, Eye, Shield, ListChecks } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

export const MASTER_FINDINGS = [
  { key: "abnormal_study", name: "Abnormal Study" },
  { key: "consolidation", name: "Consolidation" },
  { key: "atelectasis", name: "Atelectasis" },
  { key: "pulmonary_edema", name: "Pulmonary Edema" },
  { key: "nodule_mass_lesion", name: "Nodule / Mass Lesion" },
  { key: "ggo", name: "Ground Glass Opacities (GGO)" },
  { key: "cavitating_lesion", name: "Cavitating Lesion" },
  { key: "fibrosis_interstitial_opacities", name: "Fibrosis / Interstitial Opacities" },
  { key: "calcific_foci", name: "Calcific Foci (e.g., Granulomas)" },
  { key: "hyperinflation_poor_aeration", name: "Hyperinflation / Poor Aeration" },
  { key: "pleural_effusion", name: "Pleural Effusion (R/L/Bilateral)" },
  { key: "pneumothorax", name: "Pneumothorax (R/L)" },
  { key: "pleural_thickening", name: "Pleural Thickening" },
  { key: "pleural_calcification", name: "Pleural Calcification" },
  { key: "hemothorax", name: "Hemothorax" },
  { key: "cardiomegaly", name: "Cardiomegaly (Increased CTR)" },
  { key: "aortic_calcification", name: "Aortic Atherosclerosis / Calcification" },
  { key: "pulmonary_vascular_prominence", name: "Prominent Pulmonary Vasculature" },
  { key: "mediastinal_widening", name: "Mediastinal Widening" },
  { key: "lymphadenopathy", name: "Hilar / Mediastinal Lymphadenopathy" },
  { key: "tracheal_deviation", name: "Tracheal Shift / Deviation" },
  { key: "rib_fractures", name: "Rib Fractures" },
  { key: "sternal_sutures", name: "Sternal Sutures (Post-Thoracotomy)" },
  { key: "soft_tissue_abnormality_emphysema", name: "Soft Tissue Abnormality / Emphysema" },
  { key: "endotracheal_tube", name: "Endotracheal Tube (ETT)" },
  { key: "central_venous_line", name: "Central Venous Line" },
  { key: "intercostal_drainage_tube", name: "Intercostal Drainage (ICD) Tube" },
  { key: "tracheostomy_tube", name: "Tracheostomy Tube" },
  { key: "overall_abnormal_study", name: "Overall Abnormal Study" },
  { key: "suspicion_of_active_tb", name: "Suspicion of Active Tuberculosis (TB)" },
  { key: "suspicion_of_cvd", name: "Suspicion of Cardiovascular Disease (CVD)" },
];

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b"];

export default function AdminOverview() {
  const [metrics, setMetrics] = useState<AiMetric[]>([]);
  const [summary, setSummary] = useState<AiSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [evaluatorsLoading, setEvaluatorsLoading] = useState(false);
  const [selectedEvaluator, setSelectedEvaluator] = useState("");

  const fetchMetrics = async (username?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await AdminAPI.getMetrics(undefined, username);
      setMetrics(data || []);
    } catch (err) {
      console.error("Failed to fetch metrics:", err);
      setError("Failed to load metrics data");
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluators = async () => {
    try {
      setEvaluatorsLoading(true);
      const data = await AdminAPI.getEvaluators();
      setEvaluators(data || []);
    } catch (err) {
      console.error("Failed to fetch evaluators:", err);
      setEvaluators([]);
    } finally {
      setEvaluatorsLoading(false);
    }
  };

  const fetchSummary = async (username?: string) => {
    try {
      const data = await AdminAPI.getSummary(username);
      setSummary(data);
    } catch (err) {
      console.warn("Failed to fetch summary:", err);
      setSummary(null);
    }
  };

  useEffect(() => {
    fetchMetrics(selectedEvaluator || undefined);
    fetchSummary(selectedEvaluator || undefined);
    fetchEvaluators();
    const interval = setInterval(() => {
      fetchMetrics(selectedEvaluator || undefined);
      fetchSummary(selectedEvaluator || undefined);
    }, 35000);
    return () => clearInterval(interval);
  }, [selectedEvaluator]);

  const averages = useMemo(() => {
    if (!metrics.length)
      return { sensitivity: 0, specificity: 0, accuracy: 0 };
    // only include labels that actually had data
    const valid = metrics.filter(
      (m) => (m.tp + m.fn + m.fp + m.tn) > 0
    );
    if (!valid.length)
      return { sensitivity: 0, specificity: 0, accuracy: 0 };

    const totals = valid.reduce(
      (acc, m) => ({
        sensitivity: acc.sensitivity + m.sensitivity,
        specificity: acc.specificity + m.specificity,
        accuracy: acc.accuracy + m.accuracy,
      }),
      { sensitivity: 0, specificity: 0, accuracy: 0 }
    );

    return {
      sensitivity: totals.sensitivity / valid.length,
      specificity: totals.specificity / valid.length,
      accuracy: totals.accuracy / valid.length,
    };
  }, [metrics]);

  const chartData = [
    { name: "Sensitivity", value: averages.sensitivity * 100 },
    { name: "Specificity", value: averages.specificity * 100 },
    { name: "Accuracy", value: averages.accuracy * 100 },
  ];

  const kpiCards = [
    {
      title: "Total Studies",
      value: summary?.total_studies?.toString() || "—",
      icon: <HardDrive className="h-5 w-5 text-muted-foreground" />,
    },
    {
      title: "Labels Extracted",
      value: summary?.labels_extracted?.toString() || "—",
      icon: <FileText className="h-5 w-5 text-muted-foreground" />,
    },
    {
      title: "Total Findings",
      value: summary?.total_findings?.toString() || "—",
      icon: <ListChecks className="h-5 w-5 text-muted-foreground" />,
    },
    {
      title: "Avg Sensitivity",
      value: `${(averages.sensitivity * 100).toFixed(1)}%`,
      icon: <Eye className="h-5 w-5 text-muted-foreground" />,
    },
    {
      title: "Avg Specificity",
      value: `${(averages.specificity * 100).toFixed(1)}%`,
      icon: <Shield className="h-5 w-5 text-muted-foreground" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Overview</h1>
        <p className="text-muted-foreground">
          System-wide AI vs Radiologist performance
        </p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor="overview-evaluator-filter">
            Evaluator
          </label>
          <select
            id="overview-evaluator-filter"
            aria-label="Evaluator filter"
            value={selectedEvaluator}
            onChange={(e) => setSelectedEvaluator(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            disabled={evaluatorsLoading}
          >
            <option value="">All Users</option>
            {evaluators.length === 0 && (
              <option value="" disabled>
                No evaluators available
              </option>
            )}
            {evaluators.map((evaluator) => (
              <option key={evaluator.id} value={evaluator.username}>
                {evaluator.username}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 text-red-700">
            {error}
          </CardContent>
        </Card>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {kpiCards.map((card) => (
          <StatCard
            key={card.title}
            icon={card.icon}
            title={card.title}
            value={card.value}
          />
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Average Performance</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Loading chart…
            </div>
          ) : metrics.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              No data available. Run evaluation to see results.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(value: number | undefined) => value !== undefined ? `${value.toFixed(1)}%` : 'N/A'} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS[index]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {metrics.length > 0 && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Overall Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-sm text-muted-foreground">Avg Sensitivity</div>
                  <div className="text-2xl font-semibold text-blue-600">
                    {(averages.sensitivity * 100).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Avg Specificity</div>
                  <div className="text-2xl font-semibold text-green-600">
                    {(averages.specificity * 100).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Findings</div>
                  <div className="text-2xl font-semibold text-purple-600">
                    {metrics.length}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Cases</div>
                  <div className="text-2xl font-semibold text-orange-600">
                    {metrics.reduce((sum, m) => sum + (m.tp + m.fp + m.tn + m.fn), 0)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Findings Performance Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left font-medium">Finding</th>
                      <th className="p-3 text-center font-medium">TP</th>
                      <th className="p-3 text-center font-medium">FP</th>
                      <th className="p-3 text-center font-medium">TN</th>
                      <th className="p-3 text-center font-medium">FN</th>
                      <th className="p-3 text-center font-medium">Sensitivity</th>
                      <th className="p-3 text-center font-medium">Specificity</th>
                      <th className="p-3 text-center font-medium">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MASTER_FINDINGS.map((d) => {
                      const m = metrics.find((x) => x.label === d.key);
                      const tp = m?.tp ?? 0;
                      const fp = m?.fp ?? 0;
                      const tn = m?.tn ?? 0;
                      const fn = m?.fn ?? 0;
                      const sensitivity = m?.sensitivity ?? 0;
                      const specificity = m?.specificity ?? 0;
                      const accuracy = m?.accuracy ?? 0;

                      return (
                        <tr key={d.key} className="border-t hover:bg-muted/50">
                          <td className="p-3 font-medium">{d.name}</td>
                          <td className="p-3 text-center text-green-700 font-medium">{tp}</td>
                          <td className="p-3 text-center text-red-700 font-medium">{fp}</td>
                          <td className="p-3 text-center text-green-700 font-medium">{tn}</td>
                          <td className="p-3 text-center text-red-700 font-medium">{fn}</td>
                          <td className="p-3 text-center">{(sensitivity * 100).toFixed(1)}%</td>
                          <td className="p-3 text-center">{(specificity * 100).toFixed(1)}%</td>
                          <td className="p-3 text-center">{(accuracy * 100).toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function StatCard({
  title,
  value = "—",
  icon,
}: {
  title: string;
  value?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          {icon && <div className="flex items-center">{icon}</div>}
          <CardTitle className="text-sm">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="text-2xl font-bold">{value}</CardContent>
    </Card>
  );
}
